/**
 * Following a detached `scripts.run` to its outcome (papan-ai/papan#471).
 *
 * `POST /api/v1/scripts/run` answers a slow run with `202 { queued, jobId }`
 * instead of holding the request open past the proxy's ~100s ceiling. This is
 * the client half: poll `GET /run-status/:jobId` until it reports something
 * terminal, and hand back the same `RunResult` the inline path would have
 * returned — so `papan.scripts.run()` keeps its exact promise contract and no
 * app changes.
 *
 * ── ONE implementation, two callers ─────────────────────────────────────────
 *
 * The npm SDK uses it for external ppn.gg pages; `HtmlViewer` uses it for the
 * iframe relay, where the HOST performs the fetch on the app's behalf. Two
 * copies of "when has this finished?" is how a page and its host end up
 * disagreeing about whether a run is still going. Same reason
 * `transport-error.ts` is shared with that file rather than duplicated.
 *
 * ── The socket leads; the poll is a backstop ────────────────────────────────
 *
 * When the caller supplies `notifier`, a completed run arrives as a push on the
 * WebSocket that is already open — the SPA's tenant socket, or the app SDK's own
 * — and the poll drops to a slow safety net. Normal path: zero or one request
 * instead of ten.
 *
 * The poll is NOT removed, and must not be. Pub/sub is fire-and-forget: a socket
 * that is down or mid-reconnect when the job finishes drops the frame, and a
 * client with no fallback then waits forever on a run that SUCCEEDED — which is
 * precisely the failure this whole issue exists to remove, re-created one layer
 * out. The realtime layer already learned this once; its file events carry a
 * resync handshake for the same reason.
 *
 * ── Timing out here is NOT a failure ────────────────────────────────────────
 *
 * Running out of patience means we stopped watching, never that the script
 * stopped. Reporting a still-running script as failed is the entire defect this
 * issue exists to remove; re-creating it in the poller would be the same bug one
 * layer out. So the deadline yields `response_lost` — the code that already
 * means "this may have applied, check before retrying" — and never a
 * `script_error`.
 */
import { RESPONSE_LOST, RESPONSE_LOST_DETAIL } from './transport-error.js'

/** The 202 body the route hands back when a run outlives its bounded wait. */
export interface QueuedRun { queued: true; jobId: string }

export function isQueuedRun(v: unknown): v is QueuedRun {
  return !!v && typeof v === 'object'
    && (v as QueuedRun).queued === true
    && typeof (v as QueuedRun).jobId === 'string'
}

/** What `GET /run-status/:jobId` reports. `refused` replays a 4xx refusal. */
export interface RunStatusBody {
  state: 'queued' | 'running' | 'completed' | 'refused' | 'failed' | 'expired'
  result?: Record<string, unknown>
  /** Present on `refused` — the status the inline path would have used. */
  status?: number
  /** Present on `failed` — why the JOB failed, not why the script did. */
  error?: string
}

/**
 * The header that opts a client into the 202 (D4).
 *
 * Without it the route behaves exactly as it did before this change. An
 * externally pinned old SDK bundle would read `{queued:true}` as a falsy `ok`
 * and report a failure for a run that is merely still going — a new failure
 * mode for apps that changed nothing.
 */
export const ASYNC_RUNS_HEADER = 'X-Papan-Async-Runs'
export const ASYNC_RUNS_VALUE = '1'

/**
 * How long the client follows a detached run before giving up on WATCHING it.
 *
 * Generous, because work that legitimately takes minutes is the entire point.
 * It is not a ceiling on the run.
 */
export const RUN_POLL_DEADLINE_MS = 10 * 60 * 1000

/** Fetch one status page. Returns `null` when the poll itself was refused. */
export type RunStatusFetcher = (jobId: string) => Promise<RunStatusBody | null>

/**
 * Is this HTTP status a VERDICT on the run, or just a bad moment?
 *
 * `null` from a fetcher ends the poll and reports `response_lost`, so the answer
 * decides whether a live run is abandoned. Only a status that says "this job is
 * not readable by you, and asking again will not change that" qualifies:
 *
 *   401 the session is gone · 403 not yours · 404 unknown or expired · 410 gone
 *
 * EVERYTHING else — 429, 500, 502, 503, 504, a proxy's 52x — is transient and
 * must be retried. Collapsing them into "terminal" is the defect this whole
 * issue exists to remove, wearing the poller's clothes: one 429 from the API's
 * own rate limit, or one 502 while an instance restarts, and a multi-minute
 * script that is running perfectly well is reported to the app as a lost result.
 * A thrown fetch is already retried; an HTTP error is not different in kind.
 *
 * Exported so both fetchers — the npm SDK's and `HtmlViewer`'s — classify
 * identically. Two copies of "has this failed?" is how a page and its host end
 * up disagreeing about whether a run is still going.
 */
export function isTerminalPollStatus(status: number): boolean {
  return status === 401 || status === 403 || status === 404 || status === 410
}

/**
 * Subscribe to the push that says this run is done.
 *
 * Returns an unsubscribe. The callback takes no argument on purpose: the frame
 * carries no result, so being woken means "ask again now", never "here is the
 * answer". Keeping the socket a doorbell is what stops it becoming a second
 * read path with its own, weaker, authorization.
 */
export type RunNotifier = (jobId: string, onDone: () => void) => () => void

/**
 * Poll cadence with a notifier attached.
 *
 * Long, because these are the intervals at which we ask "did we miss the
 * doorbell?" — not the intervals at which we expect an answer. The first check
 * is soon enough to cover a frame lost in the seconds around the 202; after
 * that, once every half minute is plenty for a socket that is genuinely down.
 */
const BACKSTOP_FIRST_MS = 15_000
const BACKSTOP_EVERY_MS = 30_000

/**
 * Poll a queued run to its terminal state.
 *
 * `body` is the run's own answer, ready to hand to the app; `status` is the
 * HTTP status the inline path would have carried, which the iframe relay needs
 * in order to keep driving the 412 consent dance identically.
 */
export async function pollRun(
  jobId: string,
  fetchStatus: RunStatusFetcher,
  opts: {
    deadlineMs?: number
    sleep?: (ms: number) => Promise<void>
    /** The push. Supply it and the poll becomes a backstop; omit it and the
     *  cadence is unchanged, so a caller with no socket is unaffected. */
    notifier?: RunNotifier
  } = {},
): Promise<{ status: number; body: Record<string, unknown> }> {
  const deadlineMs = opts.deadlineMs ?? RUN_POLL_DEADLINE_MS
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const startedAt = Date.now()
  // Without a notifier: back off to 5s. A multi-minute script must not be polled
  // 600 times, while a run finishing just past the server's wait still answers
  // promptly — the ramp starts at 300ms, so the first few checks are quick and
  // only a genuinely long run reaches the ceiling.
  //
  // 5s rather than 2s because the steady-state cost is what a rate limit sees:
  // 12 requests a minute per run instead of 30, so several concurrent detached
  // runs stay comfortably inside the poll tier. The deadline is unchanged; this
  // costs a few seconds of latency on the slowest runs and nothing on any other.
  let delay = 300

  // Subscribe BEFORE the first sleep. The run can finish during that sleep, and
  // a subscription opened afterwards would miss the frame and fall through to
  // the slow backstop — turning the fast path into the slow one exactly when the
  // run was fast.
  let wake: (() => void) | null = null
  let woken = false
  const unsubscribe = opts.notifier
    ? opts.notifier(jobId, () => { woken = true; wake?.() })
    : null

  /** Sleep, but cut it short the moment the push arrives. */
  const waitForNextCheck = async (ms: number): Promise<void> => {
    if (!unsubscribe) return sleep(ms)
    if (woken) { woken = false; return }
    await new Promise<void>((resolve) => {
      let settled = false
      const done = () => { if (!settled) { settled = true; wake = null; resolve() } }
      wake = done
      void sleep(ms).then(done)
    })
    woken = false
  }

  try {
  for (;;) {
    if (unsubscribe) {
      // First backstop check is sooner than the rest — it covers a frame lost in
      // the seconds around the handover, which is the likeliest moment to miss one.
      await waitForNextCheck(Date.now() - startedAt < BACKSTOP_FIRST_MS ? BACKSTOP_FIRST_MS : BACKSTOP_EVERY_MS)
    } else {
      await sleep(delay)
      delay = Math.min(delay * 1.5, 5000)
    }

    const status = await fetchStatus(jobId)
    if (status === null) {
      // The poll was refused — unknown job, or its record expired. That does
      // NOT say the run failed; most likely it succeeded and we are simply too
      // late to read the outcome.
      return { status: 200, body: lostBody() }
    }
    if (status.state === 'completed') {
      // A completed run with no result is an anomaly rather than a clean empty
      // outcome — surface it instead of returning a silent success.
      return status.result
        ? { status: 200, body: status.result }
        : { status: 200, body: lostBody() }
    }
    if (status.state === 'refused') {
      return { status: status.status ?? 400, body: status.result ?? { ok: false, error: 'refused' } }
    }
    if (status.state === 'failed') {
      // The JOB failed — infrastructure. The server sends a run-shaped `result`
      // for this (carrying `error: 'job_failed'` and, for an author, the
      // notice); prefer it, so the two halves of the same outcome are not
      // described differently depending on whether the run finished inside the
      // request's bounded wait or after it.
      //
      // The fallback is `response_lost` rather than a script failure: the work
      // may or may not have applied, and an app must check before retrying
      // rather than assume either way.
      return status.result
        ? { status: 500, body: status.result }
        : { status: 500, body: { ...lostBody(), stderr: status.error ?? 'Script run failed' } }
    }
    if (status.state === 'expired') {
      return { status: 200, body: lostBody() }
    }
    if (Date.now() - startedAt > deadlineMs) {
      return { status: 200, body: lostBody() }
    }
  }
  } finally {
    // Always, on every exit path. A leaked subscription holds a handler per run
    // for the life of the page.
    unsubscribe?.()
  }
}

function lostBody(): Record<string, unknown> {
  // The SAME spelling `transportErrorBody` uses, deliberately. Its operative
  // half — "the run may still be executing; check its results before retrying"
  // — is exactly true of every branch that lands here, and one wording across
  // every surface is what that module asks for.
  return { ok: false, error: RESPONSE_LOST, detail: RESPONSE_LOST_DETAIL }
}

/**
 * A fresh idempotency handle for one `scripts.run` call (#471 D7).
 *
 * `crypto.randomUUID` where the runtime has it (every browser this SDK targets,
 * over HTTPS), with a random fallback for the http-origin and older-runtime
 * cases where it is undefined — a missing handle is not fatal, it merely means
 * a retried enqueue starts a second run, which is exactly today's behaviour.
 */
export function mintRunId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (typeof c?.randomUUID === 'function') return c.randomUUID()
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}
